import React, { useEffect, useRef, useState } from "react";
import './Pages/PagesCss/Home.css';
import { useNavigate } from "react-router-dom";

/* ── data ── */
const products = [
  {
    id: "01",
    name: "AI & Automation",
    tag: "Focus Area",
    desc: "We build intelligent tools that automate repetitive tasks, streamline workflows, and give people more time to focus on what matters.",
    status: "Core Principle",
    route: "/AIAutomation",
  },
  {
    id: "02",
    name: "In-House Development",
    tag: "Approach",
    desc: "Every product is conceived, designed, and developed entirely by D-Tech Studios, ensuring full ownership, quality, and innovation.",
    status: "Core Principle",
    route: "/InHouseDevelopment",
  },
  {
    id: "03",
    name: "User-Centric Design",
    tag: "Philosophy",
    desc: "We focus on real users, designing software that is simple, intuitive, and solves genuine problems.",
    status: "Core Principle",
    route: "/UserCentricDesign",
  },
  {
    id: "04",
    name: "Transparency & Trust",
    tag: "Value",
    desc: "Our products reflect accountability and openness users know who built them and why, which builds trust.",
    status: "Core Principle",
    route: "/TransparencyTrust",
  },
];

const principles = [
  {
    n: "01",
    title: "Full Ownership",
    body: "Every product is conceived, designed, and developed entirely in-house at D-Tech Studios. We take complete responsibility from concept to launch.",
  },
  {
    n: "02",
    title: "Eliminate Friction",
    body: "We identify repetitive or time-consuming tasks and remove them with intelligent, seamless solutions that make work easier.",
  },
  {
    n: "03",
    title: "Transparent by Design",
    body: "Users know exactly who created our software and why. Accountability and clarity are at the core of our approach.",
  },
];

/* ── Agent log entries ── */
const AGENT_LOGS = [
  { type: "system",  text: "D-Tech Agent Network · Online",          delay: 0 },
  { type: "task",    text: "Processing: content-pipeline-v2",         delay: 600 },
  { type: "ok",      text: "Fetched 1,204 data sources",              delay: 1300 },
  { type: "task",    text: "Running: sentiment-classifier",           delay: 2100 },
  { type: "ok",      text: "Classified 3,890 records  [98.2% acc]",   delay: 2900 },
  { type: "task",    text: "Trigger: auto-report scheduled",          delay: 3700 },
  { type: "ok",      text: "Report delivered → 14 recipients",        delay: 4500 },
  { type: "info",    text: "Idle time recovered: 48 hrs / week",      delay: 5400 },
  { type: "system",  text: "All agents nominal  ●",                   delay: 6200 },
];

/* ── hook: intersection reveal ── */
function useReveal() {
  const [seen, setSeen] = useState(new Set());
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) setSeen((p) => new Set([...p, e.target.dataset.key]));
      }),
      { threshold: 0.1 }
    );
    document.querySelectorAll("[data-key]").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return (k) => seen.has(k);
}

/* ── ticker ── */
const TICKS = ["AI Automation", "SaaS Products", "Innovation", "Daily Tools", "AI Agents", "Zero Friction"];

/* ── Agent Terminal ── */
function AgentTerminal() {
  const [visibleCount, setVisibleCount] = useState(0);
  const [cursor, setCursor] = useState(true);
  const bodyRef = useRef(null);

  // reveal log lines one by one
  useEffect(() => {
    const timers = AGENT_LOGS.map((log, i) =>
      setTimeout(() => setVisibleCount(i + 1), log.delay)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  // blinking cursor
  useEffect(() => {
    const id = setInterval(() => setCursor(c => !c), 530);
    return () => clearInterval(id);
  }, []);

  // auto-scroll
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [visibleCount]);

  const prefix = {
    system: "◈ SYS",
    task:   "▶ RUN",
    ok:     "✓ OUT",
    info:   "ℹ INF",
  };

  return (
    <div className="h-terminal">
      {/* terminal top bar */}
      <div className="h-term-bar">
        <div className="h-term-dots">
          <span className="h-term-dot h-td-r" />
          <span className="h-term-dot h-td-y" />
          <span className="h-term-dot h-td-g" />
        </div>
        <span className="h-term-title">agent-runner  —  bash</span>
        <span className="h-term-badge">● LIVE</span>
      </div>

      {/* prompt path */}
      <div className="h-term-path">
        <span className="h-tp-dir">~/d-tech/agents</span>
        <span className="h-tp-branch"> git:(main)</span>
      </div>

      {/* log body */}
      <div className="h-term-body" ref={bodyRef}>
        {AGENT_LOGS.slice(0, visibleCount).map((log, i) => (
          <div className={`h-tl h-tl-${log.type}`} key={i}>
            <span className="h-tl-pre">{prefix[log.type]}</span>
            <span className="h-tl-txt">{log.text}</span>
          </div>
        ))}
        {/* blinking cursor on last visible line */}
        {visibleCount < AGENT_LOGS.length && (
          <div className="h-tl h-tl-cursor">
            <span className="h-tl-pre">▶ RUN</span>
            <span className="h-tl-txt">
              <span className={`h-cursor ${cursor ? "h-cursor-on" : ""}`}>█</span>
            </span>
          </div>
        )}
      </div>

      {/* bottom status bar */}
      <div className="h-term-foot">
        <span className="h-tf-item">agents: 12 active</span>
        <span className="h-tf-sep">·</span>
        <span className="h-tf-item">queue: 0</span>
        <span className="h-tf-sep">·</span>
        <span className="h-tf-item h-tf-ok">all systems nominal</span>
      </div>

      {/* floating mini-stat chips */}
      <div className="h-term-chips">
        <div className="h-term-chip">
          <span className="h-tc-val">1,204</span>
          <span className="h-tc-lbl">tasks done</span>
        </div>
        <div className="h-term-chip">
          <span className="h-tc-val">48h</span>
          <span className="h-tc-lbl">saved / wk</span>
        </div>
        <div className="h-term-chip">
          <span className="h-tc-val">98.2%</span>
          <span className="h-tc-lbl">accuracy</span>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const vis = useReveal();
  const [tick, setTick] = useState(0);
  const [tickFade, setTickFade] = useState(true);
  const navigate = useNavigate();

  /* rotating word */
  useEffect(() => {
    const id = setInterval(() => {
      setTickFade(false);
      setTimeout(() => {
        setTick((t) => (t + 1) % TICKS.length);
        setTickFade(true);
      }, 350);
    }, 2200);
    return () => clearInterval(id);
  }, []);

  /* animated counter */
  const Counter = ({ to, suffix = "" }) => {
    const [n, setN] = useState(0);
    const ref = useRef();
    useEffect(() => {
      const io = new IntersectionObserver(([e]) => {
        if (e.isIntersecting) {
          io.disconnect();
          let start = 0;
          const step = Math.ceil(to / 60);
          const id = setInterval(() => {
            start = Math.min(start + step, to);
            setN(start);
            if (start >= to) clearInterval(id);
          }, 18);
        }
      }, { threshold: 0.5 });
      if (ref.current) io.observe(ref.current);
      return () => io.disconnect();
    }, [to]);
    return <span ref={ref}>{n}{suffix}</span>;
  };

  return (
    <div className="h-root">

      {/* ══ HERO ══ */}
      <section className="h-hero">
        {/* left column */}
        <div className="h-hero-l">
          <p className="h-eyebrow">
            <span className="h-eyebrow-line" />
            Independent AI &amp; SaaS Studio
          </p>

          <h1 className="h-h1">
            We build<br />
            software that<br />
            <span className="h-h1-rotate" style={{ opacity: tickFade ? 1 : 0 }}>
              {TICKS[tick]}.
            </span>
          </h1>

          <p className="h-hero-body">
            D-Tech Studios crafts AI-powered tools that eliminate repetitive work,
            accelerate workflows, and give creators and businesses back their most
            valuable asset time. Every product, built entirely in-house.
          </p>

          <div className="h-hero-btns">
            <button className="h-btn-dark"
            onClick={() => navigate("/Products")}
            >
              See Our Products
              <span className="h-btn-arrow">→</span>
            </button>
            <button className="h-btn-bare"
            onClick={() => navigate("/About")}
            >
              About the Studio</button>
          </div>

          <div className="h-hero-chips">
            {products.map((p) => (
              <span className="h-chip" key={p.id}>{p.name}</span>
            ))}
          </div>
        </div>

        {/* right column Agent Terminal */}
        <div className="h-hero-r">
          <AgentTerminal />
        </div>
      </section>

      {/* ══ MARQUEE ══ */}
      <div className="h-marquee">
        <div className="h-marquee-track">
          {[...TICKS, ...TICKS, ...TICKS].map((t, i) => (
            <span key={i} className="h-marquee-item">
              {t}
              <span className="h-marquee-sep">·</span>
            </span>
          ))}
        </div>
      </div>

      {/* ══ WHO WE ARE ══ */}
      <section
        className={`h-section h-who ${vis("who") ? "h-in" : ""}`}
        data-key="who"
      >
        <div className="h-who-inner">
          <div className="h-who-l">
            <p className="h-tag">Who we are</p>
            <h2 className="h-h2">
              A studio that<br />
              <em>turns its ideas into fully realized products.</em>
            </h2>
          </div>
          <div className="h-who-r">
            <p>
              D-Tech Studios builds AI-powered products that transform how people work and create.
              Every app and tool is conceived, designed, and developed entirely in-house, reflecting
              our dedication to innovation, reliability, and seamless user experiences. We take ownership
              of every stage from idea to execution ensuring each product is crafted to solve real-world problems.
            </p>
            <p>
              Our focus is on AI, automation, and productivity. We design software that removes repetitive
              tasks, streamlines workflows, and enhances efficiency. Every product we release embodies
              our commitment to quality, transparency, and giving users more control over their time and results.
            </p>
            <button className="h-btn-outline"
            onClick={() => navigate("/About")}
            >
              Read our story →</button>
          </div>
        </div>
      </section>

      {/* ══ STATS ══ */}
      <section
        className={`h-stats-section ${vis("stats") ? "h-in" : ""}`}
        data-key="stats"
      >
        <div className="h-stats-grid">
          {[
            { n: 4,   s: "+",  label: "Flagship SaaS products" },
            { n: 100, s: "K+", label: "Tasks automated monthly" },
            { n: 3,   s: "×",  label: "Avg. time saved per user" },
            { n: 100, s: "%",  label: "In-house. Always." },
          ].map((s, i) => (
            <div className="h-stat" key={i}>
              <strong className="h-stat-n">
                <Counter to={s.n} suffix={s.s} />
              </strong>
              <span className="h-stat-l">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ══ PRODUCTS ══ */}
      <section
        className={`h-section h-products ${vis("products") ? "h-in" : ""}`}
        data-key="products"
      >
        <div className="h-prod-head">
          <p className="h-tag">Our Focus</p>
          <h2 className="h-h2">What defines D-Tech Studios.</h2>
        </div>

        <div className="h-prod-grid">
          {products.map((p, i) => (
            <article
              className="h-pcard"
              key={p.id}
              style={{ transitionDelay: `${i * 0.08}s` }}
            >
              <div className="h-pcard-top">
                <span className="h-pcard-id">{p.id}</span>
                <span className={`h-pcard-status s-${p.status.toLowerCase()}`}>
                  {p.status}
                </span>
              </div>
              <p className="h-pcard-tag">{p.tag}</p>
              <h3 className="h-pcard-name">{p.name}</h3>
              <p className="h-pcard-desc">{p.desc}</p>
              <a className="h-pcard-link"
              onClick={() => navigate(p.route)}
              >
                Learn more <span className="h-arr">→</span>
              </a>
            </article>
          ))}
        </div>
      </section>

      {/* ══ PRINCIPLES ══ */}
      <section
        className={`h-section h-principles ${vis("principles") ? "h-in" : ""}`}
        data-key="principles"
      >
        <div className="h-prin-head">
          <p className="h-tag">Our Approach</p>
          <h2 className="h-h2">How we create intelligent software.</h2>
        </div>
        <div className="h-prin-list">
          {principles.map((p, i) => (
            <div
              className="h-prin-item"
              key={i}
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              <span className="h-prin-n">{p.n}</span>
              <div className="h-prin-body">
                <h3>{p.title}</h3>
                <p>{p.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ══ TRUST CTA ══ */}
      <section
        className={`h-section h-cta ${vis("cta") ? "h-in" : ""}`}
        data-key="cta"
      >
        <div className="h-cta-inner">
          <div className="h-cta-mark">D</div>
          <div className="h-cta-copy">
            <h2 className="h-cta-h">
              Every creation<br />bears our name.
            </h2>
            <p>
              All D-Tech Studios products are built entirely in-house. No outsourcing, no hidden processes just a team fully accountable for its work.
            </p>
            <button className="h-btn-dark"
            onClick={() => navigate("/About")}
            >
              Meet the Studio <span className="h-btn-arrow">→</span>
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}